/**
 * Web sharing utility
 * Uses Web Share API with clipboard fallback
 */

/**
 * Check if sharing is available
 */
export const canShare = () => {
  return typeof navigator !== 'undefined' && navigator.share !== undefined;
};

/**
 * Get safe origin URL
 */
const getSafeOrigin = () => {
  try {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin || 'https://panichefs.com';
    }
    return 'https://panichefs.com';
  } catch (e) {
    console.error('Failed to get window.location.origin:', e);
    return 'https://panichefs.com';
  }
};

/**
 * Share a chef profile
 * @param {Object} chef - Chef object with _id, firstname, lastname
 * @param {Object} translations - Translation strings {title, text, dialogTitle}
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const shareChef = async (chef, translations = {}) => {
  try {
    const origin = getSafeOrigin();
    const url = `${origin}/order/chef/menu?chef=${chef._id}`;
    const title = translations.title || `Check out Chef ${chef.firstname} ${chef.lastname}`;
    const text = translations.text || `Amazing food from Chef ${chef.firstname}! Order now on Panichefs.`;
    const dialogTitle = translations.dialogTitle || "Share Chef with Friends";

    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url
      });
      return true;
    } else {
      // Fallback: Copy to clipboard
      await navigator.clipboard.writeText(`${title}\n${url}`);
      return false; // Indicate fallback was used
    }
  } catch (e) {
    console.error("Share chef failed:", e);
    return false;
  }
};

/**
 * Share a food item/dish
 * @param {Object} food - Food object with _id, name
 * @param {Object} chef - Chef object (optional)
 * @param {Object} translations - Translation strings {title, text, dialogTitle}
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const shareFood = async (food, chef = null, translations = {}) => {
  try {
    const origin = getSafeOrigin();
    const chefParam = chef ? `&chef=${chef._id}` : "";
    const url = `${origin}/order/chef/menu?food=${food._id}${chefParam}`;
    const title = translations.title || `Check out ${food.name}`;
    const text = translations.text || (chef
      ? `Delicious ${food.name} from Chef ${chef.firstname}! Order now on Panichefs.`
      : `Delicious ${food.name}! Order now on Panichefs.`);

    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url
      });
      return true;
    } else {
      await navigator.clipboard.writeText(`${title}\n${url}`);
      return false;
    }
  } catch (e) {
    console.error("Share food failed:", e);
    return false;
  }
};

/**
 * Share order experience
 * @param {Object} order - Order object (unused but kept for API compatibility)
 * @param {Object} translations - Translation strings {title, text, dialogTitle}
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const shareOrderExperience = async (order, translations = {}) => {
  try {
    const url = getSafeOrigin();
    const title = translations.title || "I just ordered from Panichefs!";
    const text = translations.text || "Check out Panichefs - amazing homemade food from local chefs delivered to your door!";

    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url
      });
      return true;
    } else {
      await navigator.clipboard.writeText(`${title}\n${url}`);
      return false;
    }
  } catch (e) {
    console.error("Share order failed:", e);
    return false;
  }
};

/**
 * Share app with referral code (if applicable)
 * @param {string} referralCode - User's referral code
 * @param {Object} translations - Translation strings {title, textWithRef, textWithoutRef, dialogTitle}
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const shareApp = async (referralCode = null, translations = {}) => {
  try {
    const origin = getSafeOrigin();
    const url = referralCode
      ? `${origin}?ref=${referralCode}`
      : origin;
    const title = translations.title || "Try Panichefs - Homemade Food Delivery";
    const text = referralCode
      ? (translations.textWithRef || `Join Panichefs with my referral code and get a special discount! Order amazing homemade food from local chefs.`)
      : (translations.textWithoutRef || `Check out Panichefs - amazing homemade food from local chefs delivered to your door!`);

    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url
      });
      return true;
    } else {
      await navigator.clipboard.writeText(`${title}\n${url}`);
      return false;
    }
  } catch (e) {
    console.error("Share app failed:", e);
    return false;
  }
};

/**
 * Generic share function
 * @param {Object} options - Share options {title, text, url, dialogTitle}
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const share = async ({ title, text, url, dialogTitle = "Share" }) => {
  try {
    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url
      });
      return true;
    } else {
      // Fallback: Copy to clipboard
      const shareText = `${title}\n${text}\n${url}`;
      await navigator.clipboard.writeText(shareText);
      return false;
    }
  } catch (e) {
    console.error("Share failed:", e);
    return false;
  }
};
